import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import ProfessionalPhoto from "../assets/ProfessionalPhoto2.png";
import CreativePhoto from "../assets/CreativePhoto.jpg";

const ModeSelector: React.FC = () => {
  const [hoveredMode, setHoveredMode] = useState<string | null>(null);

  const modes = [
    {
      id: "professional",
      title: "Professional",
      photo: ProfessionalPhoto,
      path: "/professional",
    },
    {
      id: "creative",
      title: "Creative",
      photo: CreativePhoto,
      path: "/creative",
    },
  ];

  // </> tag constellation - positioned on the left (larger, more stars)
  const codeConstellation = [
    // < (less than) - 5 points
    { x: 12, y: 28 },
    { x: 8, y: 36 },
    { x: 4, y: 45 },
    { x: 8, y: 54 },
    { x: 12, y: 62 },
    // / (slash) - 5 points
    { x: 20, y: 28 },
    { x: 18, y: 36 },
    { x: 16, y: 45 },
    { x: 14, y: 54 },
    { x: 12, y: 62 },
    // > (greater than) - 5 points
    { x: 22, y: 28 },
    { x: 26, y: 36 },
    { x: 30, y: 45 },
    { x: 26, y: 54 },
    { x: 22, y: 62 },
  ];

  // Inkwell and quill constellation - positioned on the right (outline style)
  const inkwellConstellation = [
    // Quill feather outline (curved)
    { x: 96, y: 18 },
    { x: 94, y: 22 },
    { x: 91, y: 25 },
    { x: 93, y: 20 },
    { x: 90, y: 23 },
    // Quill shaft
    { x: 88, y: 28 },
    { x: 85, y: 35 },
    { x: 82, y: 42 },
    { x: 80, y: 48 },
    // Inkwell rim (horizontal line)
    { x: 72, y: 52 },
    { x: 76, y: 52 },
    { x: 80, y: 52 },
    { x: 84, y: 52 },
    { x: 88, y: 52 },
    // Inkwell left side
    { x: 71, y: 56 },
    { x: 70, y: 62 },
    { x: 71, y: 68 },
    // Inkwell bottom curve
    { x: 74, y: 72 },
    { x: 80, y: 74 },
    { x: 86, y: 72 },
    // Inkwell right side
    { x: 89, y: 68 },
    { x: 90, y: 62 },
    { x: 89, y: 56 },
  ];

  // Random background stars
  const backgroundStars = [...Array(30)].map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
  }));

  return (
    <div className="mode-selector">
      {/* Subtle ambient background */}
      <div className="ambient-bg">
        <div className="ambient-gradient" />

        {/* Random background stars */}
        <div className="ambient-stars">
          {backgroundStars.map((star) => (
            <motion.div
              key={`bg-${star.id}`}
              className="star"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.15, 0.4, 0.15] }}
              transition={{
                duration: star.duration,
                repeat: Infinity,
                delay: star.delay,
              }}
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
              }}
            />
          ))}
        </div>

        {/* Code braces constellation */}
        <div
          className={`constellation code-constellation ${
            hoveredMode === "professional" ? "active" : ""
          }`}
        >
          {codeConstellation.map((point, i) => (
            <div
              key={`code-${i}`}
              className="constellation-star"
              style={{
                left: `${point.x}%`,
                top: `${point.y}%`,
              }}
            />
          ))}
        </div>

        {/* Inkwell & Quill constellation */}
        <div
          className={`constellation inkwell-constellation ${
            hoveredMode === "creative" ? "active" : ""
          }`}
        >
          {inkwellConstellation.map((point, i) => (
            <div
              key={`inkwell-${i}`}
              className="constellation-star"
              style={{
                left: `${point.x}%`,
                top: `${point.y}%`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="mode-selector-content">
        {/* Header */}
        <motion.header
          className="selector-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="name">Mostafa Elbehery</h1>
          <p className="former-name">(Formerly El-Beheiry)</p>
        </motion.header>

        {/* Mode Cards */}
        <div className="mode-cards">
          {modes.map((mode, index) => (
            <motion.div
              key={mode.id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.15 }}
            >
              <Link
                to={mode.path}
                className={`mode-card ${mode.id}`}
                onMouseEnter={() => setHoveredMode(mode.id)}
                onMouseLeave={() => setHoveredMode(null)}
              >
                {/* Portrait */}
                <div className="card-portrait">
                  <div className="portrait-frame">
                    <img src={mode.photo} alt={`${mode.title} portrait`} />
                  </div>
                  <div className="portrait-glow" />
                </div>

                {/* Content */}
                <div className="card-content">
                  <h2 className="card-title">{mode.title}</h2>
                </div>

                {/* Hover indicator */}
                <div className="card-indicator">
                  <span className="indicator-arrow">→</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.footer
          className="selector-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <p className="thank-you">Thank you for stopping by!</p>
        </motion.footer>
      </div>
    </div>
  );
};

export default ModeSelector;
